import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput } from "@components/Forms";
import CustomTextarea from "@components/Forms/CustomTextarea";
import { UnMatchIcon, MatchIcon } from "@components/Icons";
import Breadcrumb from "@components/UI/Breadcrumb";
import CustomSelect from "@components/UI/CustomSelect";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { LICENSE_CATEGORY_OPTIONS_NO_TRANSLATION } from "@constants/openSource";
import { TOAST } from "@constants/index";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, normalizeResponseData, uppercase } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { CHARACTER_LENGTH } from "@constants/index";

const TEST_ID = "license-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldInputTestId = (name) => tid(`field-${name}-input`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

const TOGGLE_FIELD_NAMES = {
  spdxStandardFlag: "spdx-standard",
  osiApprovalFlag: "osi-approval",
  includingInReportFlag: "including-in-report",
  copyleftFlag: "copyleft",
  deprecationFlag: "deprecation",
  updateFlag: "update",
};

export default function LicenseDetail() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { licenseSeq } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [licenseData, setLicenseData] = useState({});
  const [editingData, setEditingData] = useState({});
  const [isUnchanged, setIsUnchanged] = useState(true);
  const navigate = useNavigate();
  // API mutations
  const getLicenseData = useMutationData(
    `${queryKeys.getLicenseById}/${licenseSeq}`,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const normalizedData = normalizeResponseData(response);
          setLicenseData(normalizedData);
          setEditingData(normalizedData);
        }
      },
      onError: (error) => {
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: "/licenses" } });
        } else {
          showErrorToast(t("open-source.get-license-data-error"));
        }
        console.error("Error getting license data:", error);
      },
    },
  );

  const updateLicense = useMutationData(
    `${queryKeys.updateLicense}/${licenseSeq}`,
    { method: "PUT" },
    {
      onSuccess: async (response) => {
        if (typeof response !== "string") {
          showSuccessToast(t("open-source.license-update-success"));
          getLicenseData.mutate();
          setIsEditMode(false);
          setIsUnchanged(true);
        }
      },
      onError: (error) => {
        if (error?.response?.status === 409) {
          showErrorToast(t("status.error"), t("open-source.license-update-error-conflict"));
          return;
        }
        console.error("Error updating license:", error);
        const errorMessage = error?.response?.data || error?.message || "";
        if (errorMessage.includes("License already exists with the same license identifier.")) {
          ToastConstant(TOAST.error, t("open-source.license-already-exists"), "");
        } else {
          ToastConstant(TOAST.error, t("open-source.license-update-error"), "");
        }
      },
    },
  );

  // Effects
  useEffect(() => {
    if (licenseSeq) {
      getLicenseData.mutate();
    }
  }, [licenseSeq]);

  // Validation
  useEffect(() => {
    const dataChanged = JSON.stringify(editingData) !== JSON.stringify(licenseData);
    setIsUnchanged(!dataChanged);
  }, [editingData, licenseData]);

  // Handlers
  const handleEdit = () => {
    setIsEditMode(true);
    setEditingData({ ...licenseData });
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditingData({ ...licenseData });
  };

  const handleSave = () => {
    if (!isUnchanged) {
      updateLicense.mutate({ ...licenseData, ...editingData });
    }
  };

  const handleInputChange = (field, value) => {
    setEditingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderToggleSwitch = (field, label, value) => {
    const isEnabled = value === "Y" || value === true;
    const fieldName = TOGGLE_FIELD_NAMES[field] || field;

    return (
      <div className={`flex py-4 border-t border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId(fieldName)}>
        <div className={`w-7/12 flex-shrink-0 ${isEditMode ? "mb-2 mt-3" : "mt-1"}`}>
          <span className={`text-[0.8125rem] truncate block font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId(fieldName)}>{label}</span>
        </div>
        <div className="w-5/12 flex items-center justify-start" data-testid={fieldValueTestId(fieldName)}>
          {!isEditMode && (
            <>
              {isEnabled ? (
                <MatchIcon data-testid={tid(`field-${fieldName}-match-icon`)} />
              ) : (
                <UnMatchIcon data-testid={tid(`field-${fieldName}-unmatch-icon`)} />
              )}
            </>
          )}
          {isEditMode && (
            <label className="relative inline-flex cursor-pointer hover:cursor-pointer ms-2 items-center justify-start">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={isEnabled}
                onChange={(e) => handleInputChange(field, e.target.checked ? "Y" : "N")}
                data-testid={fieldInputTestId(fieldName)}
              />
              <div
                className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:-translate-y-1/2 after:top-1/2 after:content-[''] after:absolute after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${!isEnabled ? colors.borderPrimary : "border-none"} ${isEnabled ? colors.bgActiveToggle : colors.bgInput}`}
              ></div>
              <span className={`w-max ml-2 text-sm font-medium ${colors.textNormal}`}>
                {isEnabled ? t("common.yes") : t("common.no")}
              </span>
            </label>
          )}
        </div>
      </div>
    );
  };

  if (getLicenseData.isPending) {
    return (
      <div data-testid={tid("loading")}>
        <LoadingSpinner data-testid={tid("loading-spinner")} />
      </div>
    );
  }

  return (
    <div className={`${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header */}
      <div className="mb-6" data-testid={tid("header")}>
        {/* Breadcrumb */}
        <Breadcrumb
          data-testid={tid("breadcrumb")}
          items={[
            { label: t("navbar.license-list"), href: "/licenses" },
            {
              label: t("navbar.license-details"),
              href: null,
              onClick: isEditMode ? () => setIsEditMode(false) : undefined,
            },
            ...(isEditMode ? [{ label: t("open-source.edit-license") }] : []),
          ]}
          className="mb-4"
        />

        {/* Title and Action Buttons */}
        <div className="flex items-center justify-between" data-testid={tid("title-row")}>
          <h1 className={`text-2xl font-bold truncate ${colors.textPrimary}`} data-testid={tid("title")}>{licenseData?.licenseName || "LOADING..."}</h1>

          <div className="flex items-center space-x-3" data-testid={tid("header-actions")}>
            {isEditMode ? (
              <>
                <OutlineButton data-testid={tid("cancel-button")} onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
                <PrimaryButton
                  data-testid={tid("save-button")}
                  onClick={handleSave}
                  disabled={
                    updateLicense.isPending ||
                    !editingData.licenseName?.trim() ||
                    !editingData.licenseIdentifier?.trim() ||
                    isUnchanged
                  }
                >
                  {t("common.save-button")}
                </PrimaryButton>
              </>
            ) : (
              <PrimaryButton data-testid={tid("edit-button")} onClick={handleEdit}>{t("open-source.edit-license")}</PrimaryButton>
            )}
          </div>
        </div>
      </div>

      {/* Creator Information Header Card */}
      {!isEditMode && (
        <div className={`rounded-lg border ${colors.borderPrimary} ${colors.bgPrimary} p-6 mb-4`} data-testid={tid("meta-section")}>
          <div className="grid grid-cols-4 gap-6" data-testid={tid("meta-grid")}>
            <div data-testid={fieldContainerTestId("creator")}>
              <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("creator")}>
                {t("common.creator")}
              </label>
              <UserNameWithId name={licenseData?.createrName} id={licenseData?.createrId} layout="inline" data-testid={fieldValueTestId("creator")} />
            </div>

            <div data-testid={fieldContainerTestId("create-at")}>
              <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("create-at")}>
                {t("common.create-at")}
              </label>
              <div className={`text-sm font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("create-at")}>
                {formatDateTime(licenseData?.createdAt) || "N/A"}
              </div>
            </div>

            <div data-testid={fieldContainerTestId("last-modified")}>
              <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("last-modified")}>
                {t("common.last-modified-date")}
              </label>
              <div className={`text-sm font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("last-modified")}>
                {formatDateTime(licenseData?.updatedAt) || "N/A"}
              </div>
            </div>

            <div data-testid={fieldContainerTestId("last-modified-by")}>
              <label className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`} data-testid={fieldLabelTestId("last-modified-by")}>
                {t("common.last-modified-by")}
              </label>
              <UserNameWithId name={licenseData?.updaterName} id={licenseData?.updaterId} layout="inline" data-testid={fieldValueTestId("last-modified-by")} />
            </div>
          </div>
        </div>
      )}

      {/* Main Information Cards */}
      <div data-testid={tid("form")} className={`rounded-lg border ${colors.borderPrimary} ${colors.bgPrimary}`}>
        <div className="grid grid-cols-2">
          {/* License Information Card*/}
          <div
            className={`p-6 border-r border-b-0 border-t-0 border-l-0 border-solid ${colors.borderPrimary}`}
            data-testid={tid("license-info-section")}
          >
            <h2 className={`text-lg font-bold ${colors.textPrimary} mb-4`} data-testid={tid("license-info-title")}>
              {uppercase(t("open-source.license-information-title"))}
            </h2>

            <div className={`space-y-0 border-t border-dashed ${colors.borderPrimary} mt-4`} data-testid={tid("license-info-fields")}>
              <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-name")}>
                <div className={`w-1/5 flex-shrink-0 ${isEditMode && "mt-2"}`}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("license-name")}>
                    {t("open-source.license-name")} {isEditMode && <span className={colors.textRequired}>*</span>}
                  </span>
                </div>
                <div className="w-4/5 flex-1 min-w-0">
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("license-name")}
                      value={editingData.licenseName || ""}
                      onChange={(e) => handleInputChange("licenseName", e.target.value)}
                      placeholder={t("open-source.license-name-placeholder")}
                      size="md"
                      maxLength={CHARACTER_LENGTH.COMMENT}
                    />
                  ) : (
                    <span className={`text-sm font-medium ${colors.textMessage} break-words`} data-testid={fieldValueTestId("license-name")}>
                      {licenseData?.licenseName || "---"}
                    </span>
                  )}
                </div>
              </div>

              <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-identifier")}>
                <div className={`w-1/5 flex-shrink-0 ${isEditMode && "mt-2"}`}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("license-identifier")}>
                    {t("open-source.license-identifier")} {isEditMode && <span className={colors.textRequired}>*</span>}
                  </span>
                </div>
                <div className="w-4/5 flex-1">
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("license-identifier")}
                      value={editingData.licenseIdentifier || ""}
                      onChange={(e) => handleInputChange("licenseIdentifier", e.target.value)}
                      placeholder={t("open-source.license-identifier-placeholder")}
                      size="md"
                      maxLength={CHARACTER_LENGTH.COMMENT}
                    />
                  ) : (
                    <span className={`text-sm font-medium ${colors.textMessage} break-words`} data-testid={fieldValueTestId("license-identifier")}>
                      {licenseData?.licenseIdentifier || "---"}
                    </span>
                  )}
                </div>
              </div>

              <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-category")}>
                <div className={`w-1/5 flex-shrink-0 ${isEditMode && "mt-2"}`}>
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("license-category")}>
                    {t("open-source.license-category")}
                  </span>
                </div>
                <div className="w-4/5 flex-1">
                  {isEditMode ? (
                    <div className="relative z-10">
                      <CustomSelect
                        data-testid={fieldInputTestId("license-category")}
                        options={LICENSE_CATEGORY_OPTIONS_NO_TRANSLATION.map((opt) => ({
                          value: opt.value,
                          label: t(opt.label),
                        }))}
                        value={
                          editingData.licenseCategory
                            ? {
                                value: editingData.licenseCategory,
                                label: t(
                                  LICENSE_CATEGORY_OPTIONS_NO_TRANSLATION.find((o) => o.value === editingData.licenseCategory)
                                    ?.label || "",
                                ),
                              }
                            : null
                        }
                        onChange={(opt) => handleInputChange("licenseCategory", opt?.value || "")}
                        placeholder={t("open-source.license-category-placeholder")}
                        className="w-full"
                        menuPlacement="auto"
                        size="md"
                      />
                    </div>
                  ) : (
                    <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("license-category")}>
                      {t(LICENSE_CATEGORY_OPTIONS_NO_TRANSLATION.find((opt) => opt.value === licenseData?.licenseCategory)?.label) ||
                        "---"}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex pt-4" data-testid={fieldContainerTestId("homepage-url")}>
                <div className="w-1/5 flex-shrink-0">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("homepage-url")}>
                    {t("open-source.homepage-url")}
                  </span>
                </div>
                <div className="w-4/5 flex-1">
                  {isEditMode ? (
                    <CustomInput
                      data-testid={fieldInputTestId("homepage-url")}
                      value={editingData.homepageUrl || ""}
                      onChange={(e) => handleInputChange("homepageUrl", e.target.value)}
                      placeholder={t("open-source.homepage-url-placeholder")}
                      size="md"
                    />
                  ) : licenseData?.homepageUrl ? (
                    <a
                      href={licenseData.homepageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-sm font-medium ${colors.textComparisonLink} ${colors.hoverTextComparisonLink} underline-offset-2 break-words`}
                      data-testid={fieldValueTestId("homepage-url")}
                    >
                      {licenseData.homepageUrl}
                    </a>
                  ) : (
                    <span className={`text-sm font-medium ${colors.textMessage}`} data-testid={fieldValueTestId("homepage-url")}>---</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Technical Attributes Card*/}
          <div className="p-6" data-testid={tid("technical-attributes-section")}>
            <h2 className={`text-lg font-bold ${colors.textPrimary} mb-4`} data-testid={tid("technical-attributes-title")}>
              {uppercase(t("open-source.technical-attributes"))}
            </h2>

            <div className="space-y-0" data-testid={tid("technical-attributes-fields")}>
              {/* Left Column */}
              <div className="grid grid-cols-2 gap-6" data-testid={tid("technical-attributes-grid")}>
                <div className="me-1" data-testid={tid("technical-attributes-col-left")}>
                  {renderToggleSwitch("spdxStandardFlag", t("open-source.spdx-standard"), editingData.spdxStandardFlag)}
                  {renderToggleSwitch("osiApprovalFlag", t("open-source.osi-approval"), editingData.osiApprovalFlag)}
                  {renderToggleSwitch(
                    "includingInReportFlag",
                    t("open-source.including-in-report"),
                    editingData.includingInReportFlag,
                  )}
                </div>

                {/* Right Column */}
                <div className="ms-1" data-testid={tid("technical-attributes-col-right")}>
                  {renderToggleSwitch("copyleftFlag", t("open-source.copyleft"), editingData.copyleftFlag)}
                  {renderToggleSwitch("deprecationFlag", t("open-source.deprecation"), editingData.deprecationFlag)}
                  {renderToggleSwitch("updateFlag", t("open-source.update"), editingData.updateFlag)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* License Content Details Section */}
      <div className={`rounded-lg border ${colors.borderPrimary} ${colors.bgPrimary} p-6 mt-4`} data-testid={tid("content-section")}>
        <h2 className={`text-lg font-bold ${colors.textPrimary} mb-4`} data-testid={tid("content-title")}>
          {uppercase(t("open-source.license-content-details"))}
        </h2>

        <div className={`space-y-0 border-t border-dashed ${colors.borderPrimary} mt-4`} data-testid={tid("content-fields")}>
          <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-note")}>
            <div className="w-30 flex-shrink-0">
              <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("license-note")}>
                {t("open-source.license-note")}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              {isEditMode ? (
                <>
                  <CustomTextarea
                    data-testid={fieldInputTestId("license-note")}
                    value={editingData.licenseNote || ""}
                    onChange={(e) => handleInputChange("licenseNote", e.target.value)}
                    rows={3}
                    placeholder={t("open-source.license-note-placeholder")}
                    maxLength={CHARACTER_LENGTH.LICENSE_NOTE}
                    className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("license-note-char-count")}>
                    {(editingData.licenseNote || "").length}/{CHARACTER_LENGTH.LICENSE_NOTE}
                  </p>
                </>
              ) : (
                <div
                  className={`text-sm font-medium ${colors.textMessage}`}
                  style={{
                    maxHeight: '45em',
                    overflowY: 'auto',
                    lineHeight: '1.5',
                    wordBreak: 'break-word'
                  }}
                  title={licenseData?.licenseNote || undefined}
                  data-testid={fieldValueTestId("license-note")}
                >
                  <span className="whitespace-pre-wrap">
                    {licenseData?.licenseNote || "---"}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-text")}>
            <div className="w-30 flex-shrink-0">
              <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("license-text")}>
                {t("open-source.license-text")}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              {isEditMode ? (
                <>
                  <CustomTextarea
                    data-testid={fieldInputTestId("license-text")}
                    value={editingData.licenseText || ""}
                    onChange={(e) => handleInputChange("licenseText", e.target.value)}
                    rows={6}
                    placeholder={t("open-source.license-text-placeholder")}
                    maxLength={CHARACTER_LENGTH.LICENSE_TEXT}
                    className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("license-text-char-count")}>
                    {(editingData.licenseText || "").length}/{CHARACTER_LENGTH.LICENSE_TEXT}
                  </p>
                </>
              ) : (
                <div
                  className={`text-sm font-medium ${colors.textMessage}`}
                  style={{
                    maxHeight: '45em',
                    overflowY: 'auto',
                    lineHeight: '1.5',
                    wordBreak: 'break-word'
                  }}
                  title={licenseData?.licenseText || undefined}
                  data-testid={fieldValueTestId("license-text")}
                >
                  <span className="whitespace-pre-wrap">
                    {licenseData?.licenseText || "---"}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className={`flex py-4 border-b border-dashed ${colors.borderPrimary}`} data-testid={fieldContainerTestId("license-header")}>
            <div className="w-30 flex-shrink-0">
              <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("license-header")}>
                {t("open-source.license-header")}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              {isEditMode ? (
                <>
                  <CustomTextarea
                    data-testid={fieldInputTestId("license-header")}
                    value={editingData.licenseHeader || ""}
                    onChange={(e) => handleInputChange("licenseHeader", e.target.value)}
                    rows={3}
                    placeholder={t("open-source.license-header-placeholder")}
                    maxLength={CHARACTER_LENGTH.LICENSE_HEADER}
                    className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("license-header-char-count")}>
                    {(editingData.licenseHeader || "").length}/{CHARACTER_LENGTH.LICENSE_HEADER}
                  </p>
                </>
              ) : (
                <div
                  className={`text-sm font-medium ${colors.textMessage} `}
                  style={{
                    maxHeight: '45em',
                    overflowY: 'auto',
                    lineHeight: '1.5',
                    wordBreak: 'break-word'
                  }}
                  title={licenseData?.licenseHeader || undefined}
                  data-testid={fieldValueTestId("license-header")}
                >
                  <span className="whitespace-pre-wrap">
                    {licenseData?.licenseHeader || "---"}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex pt-4" data-testid={fieldContainerTestId("license-comment")}>
            <div className="w-30 flex-shrink-0">
              <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle}`} data-testid={fieldLabelTestId("license-comment")}>
                {t("open-source.license-comment")}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              {isEditMode ? (
                <>
                  <CustomTextarea
                    data-testid={fieldInputTestId("license-comment")}
                    value={editingData.licenseComment || ""}
                    onChange={(e) => handleInputChange("licenseComment", e.target.value)}
                    rows={3}
                    placeholder={t("open-source.license-comment-placeholder")}
                    maxLength={CHARACTER_LENGTH.LICENSE_COMMENT}
                    className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("license-comment-char-count")}>
                    {(editingData.licenseComment || "").length}/{CHARACTER_LENGTH.LICENSE_COMMENT}
                  </p>
                </>
              ) : (
                <div
                  className={`text-sm font-medium ${colors.textMessage}`}
                  style={{
                    maxHeight: '45em',
                    overflowY: 'auto',
                    lineHeight: '1.5',
                    wordBreak: 'break-word'
                  }}
                  title={licenseData?.licenseComment || undefined}
                  data-testid={fieldValueTestId("license-comment")}
                >
                  <span className="whitespace-pre-wrap">
                    {licenseData?.licenseComment || "---"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
